<template>
  <!--
    As poções do pescador, onde elas fazem efeito: na pescaria (1.2).

    Duas linhas. A de cima é o que está valendo, com o tempo que resta contado
    no relógio do servidor (a store guarda quando leu; aqui só se desconta o
    que passou). A de baixo são as garrafas do estoque — um toque bebe. A poção
    de energia em barra cheia volta com `sem_efeito`, e o bilhete diz isso em
    vez de fingir que bebeu.
  -->
  <div v-if="pocoes.length || efeitosAtivos.length" class="pescaria-acao__bilhete pocoes">
    <p class="pocoes__rotulo">{{ $t('exploration.pocoes.titulo') }}</p>

    <ul v-if="efeitosAtivos.length" class="pocoes__ativos">
      <li v-for="e in efeitosAtivos" :key="e.efeito" class="pocoes__ativo">
        <FishIcon nome="flask" tamanho="miudo" />
        <span>{{ $t(`exploration.pocoes.efeitoCurto.${e.efeito}`, { valor: e.valor }) }}</span>
        <small>{{ $t('exploration.pocoes.resta', { tempo: relogio(e.restaSegundos) }) }}</small>
      </li>
    </ul>
    <p v-else class="pocoes__nenhum">{{ $t('exploration.pocoes.nenhumAtivo') }}</p>

    <div v-if="pocoes.length" class="pocoes__garrafas">
      <FishSlot
        v-for="p in pocoes"
        :key="p.potionId"
        :item="{ id: p.potionId, nome: p.nome, imagem: p.imagem, raridade: p.raridade }"
        :quantidade="p.quantidade"
        :selecionado="escolhida === p.potionId"
        @selecionar="escolher(p)"
      />
    </div>
    <p v-if="pocaoEscolhida" class="pocoes__nota">
      <strong>{{ pocaoEscolhida.nome }}</strong> ·
      {{ efeitoEmTexto(pocaoEscolhida) }}
      <FishButton tamanho="pequeno" icone="flask" :carregando="bebendo" @click="beber(pocaoEscolhida)">
        {{ $t('exploration.pocoes.beber') }}
      </FishButton>
    </p>
    <p v-if="aviso" class="pocoes__aviso">{{ aviso }}</p>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { FishButton, FishIcon, FishSlot } from '@/shared/ui';

export default {
  name: 'PocoesDePesca',
  components: { FishButton, FishIcon, FishSlot },
  data() {
    return { escolhida: null, bebendo: false, aviso: '', agora: Date.now(), relogioId: null };
  },
  computed: {
    ...mapState('pescador', ['pocoes']),
    ...mapGetters('pescador', ['efeitosAtivos']),
    pocaoEscolhida() {
      return this.pocoes.find((p) => p.potionId === this.escolhida) || null;
    },
  },
  mounted() {
    this.$store.dispatch('pescador/carregarPocoes').catch(() => {});
    // O contador anda sozinho; o getter desconta a partir do instante da leitura.
    this.relogioId = setInterval(() => {
      this.agora = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.relogioId);
  },
  methods: {
    escolher(pocao) {
      this.aviso = '';
      this.escolhida = this.escolhida === pocao.potionId ? null : pocao.potionId;
    },
    efeitoEmTexto(p) {
      return this.$t(`exploration.workshop.pocaoEfeito.${p.efeito}`, { valor: p.valor, minutos: p.duracaoMin });
    },
    /** `restaSegundos` em `mm:ss` (ou `h:mm:ss` acima de uma hora). Depende de `agora` para re-renderizar. */
    relogio(segundos) {
      void this.agora;
      const s = Math.max(0, segundos);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;
      const dois = (n) => String(n).padStart(2, '0');
      return h > 0 ? `${h}:${dois(m)}:${dois(r)}` : `${dois(m)}:${dois(r)}`;
    },
    async beber(pocao) {
      this.bebendo = true;
      this.aviso = '';
      try {
        await this.$store.dispatch('pescador/beberPocao', pocao.potionId);
        this.$store.dispatch(
          'avisos/avisar',
          { chave: 'exploration.pocoes.bebida', parametros: { nome: pocao.nome }, icone: 'flask' },
          { root: true },
        );
        if (!this.pocoes.some((p) => p.potionId === pocao.potionId)) this.escolhida = null;
      } catch (error) {
        const codigo = error?.response?.data?.codigo;
        this.aviso =
          codigo === 'sem_efeito' ? this.$t('exploration.pocoes.energiaCheia') : this.$t('exploration.errors.generico');
      } finally {
        this.bebendo = false;
      }
    },
  },
};
</script>

<style scoped>
.pocoes {
  display: grid;
  gap: var(--e-1);
}

.pocoes__rotulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

.pocoes__ativos {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-1) var(--e-2);
}

.pocoes__ativo {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--f-1);
}

.pocoes__ativo small,
.pocoes__nenhum,
.pocoes__nota,
.pocoes__aviso {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.pocoes__aviso {
  color: var(--cor-ruim);
}

.pocoes__garrafas {
  display: grid;
  grid-template-columns: repeat(auto-fill, 56px);
  gap: var(--e-1);
}

.pocoes__garrafas > * {
  width: 56px;
  height: 56px;
}

.pocoes__nota {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--e-1);
}
</style>
